/**
 * AttributesAndSkills Component
 * 
 * A dual-panel component for managing entity attributes/skills.
 * Left panel shows available attributes (from standing data with FieldId=0).
 * Right panel shows assigned attributes (skills already assigned to the entity).
 * 
 * Features:
 * - Tree view with categories and skills
 * - Multi-select with checkboxes
 * - Add/Remove buttons to move skills between panels
 * - Drag and drop support in both directions
 * - Filter/search box to find skills
 * - Skills disappear from Available when added to Assigned
 * 
 * @example
 * const component = new AttributesAndSkills(container, {
 *   entityName: 'Contact',
 *   entityId: 12345,
 *   availableSkills: standingData.Skills.filter(s => s.FieldId === 0),
 *   assignedSkills: entitySkills,
 *   onAdd: (skills) => console.log('Added:', skills),
 *   onRemove: (skills) => console.log('Removed:', skills)
 * });
 * await component.initialize();
 */

import { IComponent } from './IComponent';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('AttributesAndSkills');

/**
 * Represents a skill in the Available or Assigned tree
 */
export interface SkillItem {
  skillId: number;
  skill: string;           // Display text
  description: string | null;
  categoryId: number;
  category: string;
  ordering: number;
  fieldId: number;
  locked?: boolean;
}

/**
 * Represents a category node in the tree
 */
export interface CategoryNode {
  categoryId: number;
  category: string;
  skills: SkillItem[];
  expanded?: boolean;
  selected?: boolean;      // Whether category checkbox is selected
}

export interface AttributesAndSkillsOptions {
  /** Available skills from standing data (typically FieldId=0 for general attributes) */
  availableSkills: SkillItem[];
  
  /** Already assigned skills to this entity */
  assignedSkills: SkillItem[];
  
  /** Callback when skills are added (moved from Available to Assigned) */
  onAdd?: (skills: SkillItem[]) => void;
  
  /** Callback when skills are removed (moved from Assigned to Available) */
  onRemove?: (skills: SkillItem[]) => void;
  
  /** Enable drag and drop between panels */
  draggable?: boolean;
  
  /** Panel titles */
  availableTitle?: string;
  assignedTitle?: string;
}

export class AttributesAndSkills implements IComponent {
  private container: HTMLElement;
  private options: AttributesAndSkillsOptions;
  
  // Internal state
  private availableCategories: CategoryNode[] = [];
  private assignedCategories: CategoryNode[] = [];
  private selectedAvailable: Set<number> = new Set();   // Selected skillIds in Available panel
  private selectedAssigned: Set<number> = new Set();    // Selected skillIds in Assigned panel
  private filterText: string = '';
  
  // DOM references
  private filterInput: HTMLInputElement | null = null;
  private availableTree: HTMLElement | null = null;
  private assignedTree: HTMLElement | null = null;
  private addBtn: HTMLButtonElement | null = null;
  private removeBtn: HTMLButtonElement | null = null;
  
  constructor(container: HTMLElement, options: AttributesAndSkillsOptions) {
    this.container = container;
    this.options = {
      draggable: true,
      availableTitle: 'Available Attributes',
      assignedTitle: 'Assigned Attributes',
      ...options
    };
    
    this.buildCategoryTrees();
  }
  
  async initialize(): Promise<void> {
    this.render();
    this.bindEvents();
    logger.debug('AttributesAndSkills initialized', {
      availableCount: this.options.availableSkills.length,
      assignedCount: this.options.assignedSkills.length
    });
  }
  
  destroy(): void {
    this.container.innerHTML = '';
    this.selectedAvailable.clear();
    this.selectedAssigned.clear();
  }
  
  /**
   * Get currently assigned skills
   */
  getAssignedSkills(): SkillItem[] {
    const skills: SkillItem[] = [];
    for (const cat of this.assignedCategories) {
      skills.push(...cat.skills);
    }
    logger.debug('getAssignedSkills called', {
      categoriesCount: this.assignedCategories.length,
      skillsFromCategories: skills.length,
      optionsAssignedSkillsCount: this.options.assignedSkills.length,
      skillNames: skills.map(s => s.skill)
    });
    return skills;
  }
  
  /**
   * Set assigned skills (for external updates)
   */
  setAssignedSkills(skills: SkillItem[]): void {
    this.options.assignedSkills = skills;
    this.buildCategoryTrees();
    this.renderTrees();
    this.updateButtonStates();
  }
  
  /**
   * Build category trees from skill arrays
   * Skills assigned to entity are removed from available pool
   */
  private buildCategoryTrees(): void {
    // Preserve expanded state before rebuilding
    const availableExpandedState = new Map<number, boolean>();
    const assignedExpandedState = new Map<number, boolean>();
    
    for (const cat of this.availableCategories) {
      availableExpandedState.set(cat.categoryId, cat.expanded);
    }
    for (const cat of this.assignedCategories) {
      assignedExpandedState.set(cat.categoryId, cat.expanded);
    }
    
    const assignedIds = new Set(this.options.assignedSkills.map(s => s.skillId));
    
    // Build assigned categories (always expanded so skills are visible)
    this.assignedCategories = this.groupSkillsByCategory(this.options.assignedSkills);
    for (const cat of this.assignedCategories) {
      // Preserve previous state, default to expanded for assigned
      cat.expanded = assignedExpandedState.get(cat.categoryId) ?? true;
    }
    
    // Build available categories (excluding already assigned)
    const availableSkills = this.options.availableSkills.filter(s => !assignedIds.has(s.skillId));
    this.availableCategories = this.groupSkillsByCategory(availableSkills);
    for (const cat of this.availableCategories) {
      // Preserve previous state, default to collapsed
      cat.expanded = availableExpandedState.get(cat.categoryId) ?? false;
    }
    
    logger.debug('Category trees built', {
      availableCategories: this.availableCategories.length,
      assignedCategories: this.assignedCategories.length
    });
  }
  
  /**
   * Group skills by category
   */
  private groupSkillsByCategory(skills: SkillItem[]): CategoryNode[] {
    const categoryMap = new Map<number, CategoryNode>();
    
    for (const skill of skills) {
      let node = categoryMap.get(skill.categoryId);
      if (!node) {
        node = {
          categoryId: skill.categoryId,
          category: skill.category,
          skills: [],
          expanded: false,
          selected: false
        };
        categoryMap.set(skill.categoryId, node);
      }
      node.skills.push(skill);
    }
    
    // Sort categories alphabetically, then skills by ordering
    const categories = Array.from(categoryMap.values());
    categories.sort((a, b) => a.category.localeCompare(b.category));
    for (const cat of categories) {
      cat.skills.sort((a, b) => a.ordering - b.ordering);
    }
    
    return categories;
  }
  
  /**
   * Main render
   */
  private render(): void {
    this.container.innerHTML = `
      <div class="card bg-base-200">
        <div class="card-body p-4">
          <!-- Header with title and filter -->
          <div class="flex items-center justify-between mb-3">
            <h4 class="card-title text-sm">
              <i class="fas fa-tags text-primary mr-2"></i>Attributes
            </h4>
            <div class="join">
              <input type="text" 
                     class="input input-bordered input-sm join-item w-48" 
                     placeholder="Filter..." 
                     id="attr-filter-input" />
              <button class="btn btn-sm btn-ghost join-item" id="attr-filter-btn">
                <i class="fas fa-search"></i>
              </button>
            </div>
          </div>
          
          <!-- Three-column layout: Available | Buttons | Assigned -->
          <div class="flex gap-4">
            
            <!-- Available Attributes Panel -->
            <div class="flex-1 min-w-0">
              <div class="text-sm font-semibold mb-2 flex items-center gap-2">
                <span class="text-primary">Available</span>
                <span class="text-base-content/60">Attributes</span>
              </div>
              <div id="attr-available-tree" 
                   class="bg-base-100 rounded-lg border border-base-300 overflow-y-auto overflow-x-hidden entity-form-scrollable"
                   style="max-height: 300px; min-height: 150px;">
                <!-- Tree rendered here -->
              </div>
            </div>
            
            <!-- Center Buttons -->
            <div class="flex flex-col items-center justify-center gap-2 px-2">
              <!-- Quick add/remove all selected -->
              <div class="flex items-center gap-1 text-xs text-base-content/60 mb-2">
                <i class="fas fa-circle text-primary" style="font-size: 4px;"></i>
                <span class="mx-1">|</span>
                <i class="fas fa-circle text-secondary" style="font-size: 4px;"></i>
              </div>
              
              <button id="attr-add-btn" class="btn btn-sm btn-outline btn-primary gap-1" disabled>
                <i class="fas fa-arrow-right"></i> Add
              </button>
              <button id="attr-remove-btn" class="btn btn-sm btn-outline btn-secondary gap-1" disabled>
                <i class="fas fa-arrow-left"></i> Remove
              </button>
            </div>
            
            <!-- Assigned Attributes Panel -->
            <div class="flex-1 min-w-0">
              <div class="text-sm font-semibold mb-2 flex items-center gap-2">
                <span class="text-secondary">Assigned</span>
                <span class="text-base-content/60">Attributes</span>
              </div>
              <div id="attr-assigned-tree" 
                   class="bg-base-100 rounded-lg border border-base-300 overflow-y-auto overflow-x-hidden entity-form-scrollable"
                   style="max-height: 300px; min-height: 150px;">
                <!-- Tree rendered here -->
              </div>
            </div>
            
          </div>
        </div>
      </div>
    `;
    
    // Cache DOM references
    this.filterInput = this.container.querySelector('#attr-filter-input');
    this.availableTree = this.container.querySelector('#attr-available-tree');
    this.assignedTree = this.container.querySelector('#attr-assigned-tree');
    this.addBtn = this.container.querySelector('#attr-add-btn');
    this.removeBtn = this.container.querySelector('#attr-remove-btn');
    
    this.renderTrees();
  }
  
  /**
   * Render both trees
   */
  private renderTrees(): void {
    if (this.availableTree) {
      const availableHtml = this.renderTree(this.availableCategories, 'available');
      this.availableTree.innerHTML = availableHtml;
    }
    if (this.assignedTree) {
      const assignedHtml = this.renderTree(this.assignedCategories, 'assigned');
      this.assignedTree.innerHTML = assignedHtml;
    }
    
    this.bindTreeEvents();
  }
  
  /**
   * Render a single tree
   */
  private renderTree(categories: CategoryNode[], panel: 'available' | 'assigned'): string {
    const selectedSet = panel === 'available' ? this.selectedAvailable : this.selectedAssigned;
    const filterLower = this.filterText.toLowerCase();
    
    if (categories.length === 0) {
      return `
        <div class="flex items-center justify-center h-full text-base-content/50 text-sm">
          <i class="fas fa-inbox mr-2"></i>
          No ${panel === 'available' ? 'available' : 'assigned'} attributes
        </div>
      `;
    }
    
    let html = '<ul class="p-2">';
    
    for (const cat of categories) {
      // Filter skills
      const filteredSkills = filterLower
        ? cat.skills.filter(s => 
            s.skill.toLowerCase().includes(filterLower) ||
            s.category.toLowerCase().includes(filterLower) ||
            (s.description && s.description.toLowerCase().includes(filterLower))
          )
        : cat.skills;
      
      if (filteredSkills.length === 0 && filterLower) continue;
      
      const catId = `${panel}-cat-${cat.categoryId}`;
      const allSelected = filteredSkills.every(s => selectedSet.has(s.skillId));
      const someSelected = filteredSkills.some(s => selectedSet.has(s.skillId));
      
      html += `
        <li class="list-none mb-1">
          <details ${cat.expanded ? 'open' : ''} data-category-id="${cat.categoryId}" data-panel="${panel}">
            <summary class="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-base-200 select-none">
              <input type="checkbox" 
                     class="checkbox checkbox-sm checkbox-primary category-checkbox"
                     data-category-id="${cat.categoryId}"
                     data-panel="${panel}"
                     ${allSelected ? 'checked' : ''}
                     ${someSelected && !allSelected ? 'indeterminate' : ''} />
              <i class="fas fa-caret-right text-xs transition-transform details-arrow"></i>
              <i class="fas fa-folder${cat.expanded ? '-open' : ''} text-warning text-sm"></i>
              <span class="text-sm font-medium flex-1">${this.escapeHtml(cat.category)}</span>
              <span class="badge badge-sm badge-ghost">${filteredSkills.length}</span>
            </summary>
            <ul class="mt-1" style="margin-left: 3rem;">
      `;
      
      for (const skill of filteredSkills) {
        const isSelected = selectedSet.has(skill.skillId);
        html += `
          <li class="list-none">
            <label class="flex items-center gap-2 px-1 py-1 rounded cursor-pointer hover:bg-base-200 ${isSelected ? 'bg-base-300' : ''}"
                   draggable="${this.options.draggable}"
                   data-skill-id="${skill.skillId}"
                   data-panel="${panel}">
              <input type="checkbox" 
                     class="checkbox checkbox-xs checkbox-primary skill-checkbox"
                     data-skill-id="${skill.skillId}"
                     data-panel="${panel}"
                     ${isSelected ? 'checked' : ''} />
              <span class="text-sm">${this.escapeHtml(skill.skill)}</span>
            </label>
          </li>
        `;
      }
      
      html += `
            </ul>
          </details>
        </li>
      `;
    }
    
    html += '</ul>';
    return html;
  }
  
  /**
   * Bind events
   */
  private bindEvents(): void {
    // Filter input
    this.filterInput?.addEventListener('input', () => {
      this.filterText = this.filterInput?.value || '';
      this.renderTrees();
    });
    
    // Add button
    this.addBtn?.addEventListener('click', () => this.handleAdd());
    
    // Remove button
    this.removeBtn?.addEventListener('click', () => this.handleRemove());
  }
  
  /**
   * Bind tree-specific events (called after each render)
   */
  private bindTreeEvents(): void {
    // Category checkboxes
    const categoryCheckboxes = this.container.querySelectorAll('.category-checkbox');
    categoryCheckboxes.forEach(cb => {
      cb.addEventListener('change', (e) => this.handleCategoryCheck(e));
    });
    
    // Skill checkboxes
    const skillCheckboxes = this.container.querySelectorAll('.skill-checkbox');
    skillCheckboxes.forEach(cb => {
      cb.addEventListener('change', (e) => this.handleSkillCheck(e));
    });
    
    // Details toggle (for arrow rotation)
    const details = this.container.querySelectorAll('details[data-category-id]');
    details.forEach(d => {
      d.addEventListener('toggle', (e) => this.handleDetailsToggle(e));
    });
    
    // Drag and drop
    if (this.options.draggable) {
      this.bindDragEvents();
    }
  }
  
  /**
   * Handle category checkbox change
   */
  private handleCategoryCheck(e: Event): void {
    const target = e.target as HTMLInputElement;
    const categoryId = parseInt(target.dataset.categoryId || '0');
    const panel = target.dataset.panel as 'available' | 'assigned';
    const checked = target.checked;
    
    const categories = panel === 'available' ? this.availableCategories : this.assignedCategories;
    const selectedSet = panel === 'available' ? this.selectedAvailable : this.selectedAssigned;
    
    const category = categories.find(c => c.categoryId === categoryId);
    if (!category) return;
    
    // Select/deselect all skills in category
    for (const skill of category.skills) {
      if (checked) {
        selectedSet.add(skill.skillId);
      } else {
        selectedSet.delete(skill.skillId);
      }
    }
    
    this.renderTrees();
    this.updateButtonStates();
  }
  
  /**
   * Handle skill checkbox change
   */
  private handleSkillCheck(e: Event): void {
    e.stopPropagation(); // Don't trigger details toggle
    
    const target = e.target as HTMLInputElement;
    const skillId = parseInt(target.dataset.skillId || '0');
    const panel = target.dataset.panel as 'available' | 'assigned';
    const checked = target.checked;
    
    const selectedSet = panel === 'available' ? this.selectedAvailable : this.selectedAssigned;
    
    if (checked) {
      selectedSet.add(skillId);
    } else {
      selectedSet.delete(skillId);
    }
    
    // Update the parent category checkbox state
    this.updateCategoryCheckboxState(panel);
    this.updateButtonStates();
  }
  
  /**
   * Update category checkbox indeterminate state
   */
  private updateCategoryCheckboxState(panel: 'available' | 'assigned'): void {
    const categories = panel === 'available' ? this.availableCategories : this.assignedCategories;
    const selectedSet = panel === 'available' ? this.selectedAvailable : this.selectedAssigned;
    
    for (const cat of categories) {
      const cb = this.container.querySelector(
        `.category-checkbox[data-category-id="${cat.categoryId}"][data-panel="${panel}"]`
      ) as HTMLInputElement;
      
      if (cb) {
        const allSelected = cat.skills.every(s => selectedSet.has(s.skillId));
        const someSelected = cat.skills.some(s => selectedSet.has(s.skillId));
        
        cb.checked = allSelected;
        cb.indeterminate = someSelected && !allSelected;
      }
    }
  }
  
  /**
   * Handle details toggle for arrow rotation
   */
  private handleDetailsToggle(e: Event): void {
    const details = e.target as HTMLDetailsElement;
    const categoryId = parseInt(details.dataset.categoryId || '0');
    const panel = details.dataset.panel as 'available' | 'assigned';
    
    const categories = panel === 'available' ? this.availableCategories : this.assignedCategories;
    const category = categories.find(c => c.categoryId === categoryId);
    if (category) {
      category.expanded = details.open;
    }
    
    // Update folder icon
    const folderIcon = details.querySelector('.fa-folder, .fa-folder-open');
    if (folderIcon) {
      folderIcon.classList.toggle('fa-folder', !details.open);
      folderIcon.classList.toggle('fa-folder-open', details.open);
    }
  }
  
  /**
   * Update Add/Remove button states
   */
  private updateButtonStates(): void {
    if (this.addBtn) {
      this.addBtn.disabled = this.selectedAvailable.size === 0;
    }
    if (this.removeBtn) {
      this.removeBtn.disabled = this.selectedAssigned.size === 0;
    }
  }
  
  /**
   * Handle Add button click
   */
  private handleAdd(): void {
    if (this.selectedAvailable.size === 0) return;
    
    const skillsToAdd: SkillItem[] = [];
    
    for (const cat of this.availableCategories) {
      for (const skill of cat.skills) {
        if (this.selectedAvailable.has(skill.skillId)) {
          skillsToAdd.push(skill);
        }
      }
    }
    
    if (skillsToAdd.length === 0) return;
    
    // Move skills to assigned
    logger.debug('handleAdd: before adding', {
      optionsAssignedSkillsCount: this.options.assignedSkills.length,
      skillsToAddCount: skillsToAdd.length,
      skillsToAddNames: skillsToAdd.map(s => s.skill)
    });
    
    this.options.assignedSkills = [...this.options.assignedSkills, ...skillsToAdd];
    
    logger.debug('handleAdd: after adding', {
      optionsAssignedSkillsCount: this.options.assignedSkills.length
    });
    
    this.selectedAvailable.clear();
    
    // Rebuild trees
    this.buildCategoryTrees();
    
    logger.debug('handleAdd: after buildCategoryTrees', {
      assignedCategoriesCount: this.assignedCategories.length,
      totalSkillsInCategories: this.assignedCategories.reduce((sum, cat) => sum + cat.skills.length, 0)
    });
    
    this.renderTrees();
    this.updateButtonStates();
    
    // Notify
    logger.debug('Skills added', { count: skillsToAdd.length });
    this.options.onAdd?.(skillsToAdd);
  }
  
  /**
   * Handle Remove button click
   */
  private handleRemove(): void {
    if (this.selectedAssigned.size === 0) return;
    
    const skillsToRemove: SkillItem[] = [];
    
    for (const cat of this.assignedCategories) {
      for (const skill of cat.skills) {
        if (this.selectedAssigned.has(skill.skillId)) {
          skillsToRemove.push(skill);
        }
      }
    }
    
    if (skillsToRemove.length === 0) return;
    
    // Remove skills from assigned
    const removeIds = new Set(skillsToRemove.map(s => s.skillId));
    this.options.assignedSkills = this.options.assignedSkills.filter(s => !removeIds.has(s.skillId));
    this.selectedAssigned.clear();
    
    // Rebuild trees
    this.buildCategoryTrees();
    this.renderTrees();
    this.updateButtonStates();
    
    // Notify
    logger.debug('Skills removed', { count: skillsToRemove.length });
    this.options.onRemove?.(skillsToRemove);
  }
  
  /**
   * Bind drag and drop events
   */
  private bindDragEvents(): void {
    const draggables = this.container.querySelectorAll('[draggable="true"]');
    
    draggables.forEach(el => {
      el.addEventListener('dragstart', (e) => this.handleDragStart(e as DragEvent));
      el.addEventListener('dragend', (e) => this.handleDragEnd(e as DragEvent));
    });
    
    // Drop zones (the tree containers)
    [this.availableTree, this.assignedTree].forEach(tree => {
      if (!tree) return;
      
      tree.addEventListener('dragover', (e) => this.handleDragOver(e as DragEvent));
      tree.addEventListener('dragleave', (e) => this.handleDragLeave(e as DragEvent));
      tree.addEventListener('drop', (e) => this.handleDrop(e as DragEvent));
    });
  }
  
  private handleDragStart(e: DragEvent): void {
    const target = e.target as HTMLElement;
    const skillId = target.dataset.skillId;
    const panel = target.dataset.panel;
    
    if (!skillId || !panel) return;
    
    e.dataTransfer?.setData('text/plain', JSON.stringify({ skillId, panel }));
    target.classList.add('opacity-50');
  }
  
  private handleDragEnd(e: DragEvent): void {
    const target = e.target as HTMLElement;
    target.classList.remove('opacity-50');
  }
  
  private handleDragOver(e: DragEvent): void {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    target.classList.add('ring-2', 'ring-primary', 'ring-opacity-50');
  }
  
  private handleDragLeave(e: DragEvent): void {
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('ring-2', 'ring-primary', 'ring-opacity-50');
  }
  
  private handleDrop(e: DragEvent): void {
    e.preventDefault();
    
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('ring-2', 'ring-primary', 'ring-opacity-50');
    
    const data = e.dataTransfer?.getData('text/plain');
    if (!data) return;
    
    try {
      const { skillId, panel } = JSON.parse(data);
      const targetPanel = target.id === 'attr-available-tree' ? 'available' : 'assigned';
      
      // If dropped on same panel, do nothing
      if (panel === targetPanel) return;
      
      // Find the skill
      const sourceCategories = panel === 'available' ? this.availableCategories : this.assignedCategories;
      let skill: SkillItem | null = null;
      
      for (const cat of sourceCategories) {
        const found = cat.skills.find(s => s.skillId === parseInt(skillId));
        if (found) {
          skill = found;
          break;
        }
      }
      
      if (!skill) return;
      
      // Move skill - update options first
      if (targetPanel === 'assigned') {
        this.options.assignedSkills = [...this.options.assignedSkills, skill];
      } else {
        this.options.assignedSkills = this.options.assignedSkills.filter(s => s.skillId !== skill!.skillId);
      }
      
      // Rebuild trees BEFORE triggering callbacks so getAssignedSkills() returns correct data
      this.buildCategoryTrees();
      this.renderTrees();
      this.updateButtonStates();
      
      // NOW notify - after trees are rebuilt
      if (targetPanel === 'assigned') {
        this.options.onAdd?.([skill]);
      } else {
        this.options.onRemove?.([skill]);
      }
      
      logger.debug('Skill dropped', { skillId, from: panel, to: targetPanel });
    } catch (err) {
      logger.error('Drop handling failed', { error: err });
    }
  }
  
  /**
   * Escape HTML for safe rendering
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
